import { t } from "../core/i18n.js";
import { icon } from "./icons.js";
import { demoCursor, drawTour } from "./landing-tour.js";
import { drawProductDemo } from "./landing-product-demos.js";
import { drawGuideDemo } from "./landing-guide-demos.js";
import { drawGuideExample } from "./guide-examples.js";

export function featureDemo(kind) {
  const tasks = [
    "Read for 20 minutes",
    "Practice English",
    "Work on your main goal",
  ];
  if (kind === 0)
    return `<div class="feature-demo demo-plan" data-demo="plans" aria-hidden="true">${tasks.map((name, index) => `<div class="demo-plan-row ${index < 2 ? "done" : ""}"><span>${icon("check")}</span><div><span class="demo-task-name">${t(name)}</span><small>${["08:00 — 08:20", "09:00 — 09:25", "10:00 — 10:50"][index]}</small></div></div>`).join("")}${demoCursor}</div>`;
  if (kind === 1)
    return `<div class="feature-demo demo-focus" data-demo="focus" aria-hidden="true"><div class="demo-focus-clock"><svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="44"/><circle class="demo-focus-arc" cx="50" cy="50" r="44"/></svg><span><b data-demo-time>25:00</b><small>${t("FOCUS TIME")}</small></span></div></div>`;
  return `<div class="feature-demo demo-progress" data-demo="progress" aria-hidden="true"><div class="demo-week-bars">${[40, 63, 52, 82, 68, 92, 100].map((height, index) => `<div><i style="--height:${height}%;--delay:${index * 0.3}s"></i><span>${t(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][index])}</span></div>`).join("")}</div><div class="demo-week-cells">${Array.from({ length: 21 }, (_, index) => `<span style="--delay:${index * 0.22}s">${icon("check")}</span>`).join("")}</div></div>`;
}

let dispose;

export function bindLandingDemos(root) {
  dispose?.();
  const scenes = [...root.querySelectorAll("[data-demo]")].map((element) => ({
    element,
    elapsed: 0,
    visible: false,
    cue: -1,
    running: false,
  }));
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  let timer,
    previous = performance.now();
  const canPlay = () =>
    !motion.matches &&
    !document.hidden &&
    document.body.dataset.presentation === "home";
  const setText = (element, value) => {
    if (element.textContent !== value) element.textContent = value;
  };
  const formatTime = (seconds) =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  function positionCursor(scene, target) {
    const stage = scene.element.getBoundingClientRect();
    const bounds = target.getBoundingClientRect();
    const cursor = scene.element.querySelector(".demo-cursor");
    const x = Math.max(
      2,
      Math.min(
        bounds.left + bounds.width / 2 - stage.left,
        stage.width - cursor.offsetWidth - 2,
      ),
    );
    const y = Math.max(
      2,
      Math.min(
        bounds.top + bounds.height / 2 - stage.top,
        stage.height - cursor.offsetHeight - 2,
      ),
    );
    cursor.style.transform = `translate(${x}px, ${y}px)`;
  }

  function moveCursor(scene, target, cue, visible, clicking) {
    const cursor = scene.element.querySelector(".demo-cursor");
    cursor.classList.toggle("is-visible", visible);
    cursor.classList.toggle("is-clicking", clicking && visible);
    if (scene.cue !== cue) {
      scene.cue = cue;
      positionCursor(scene, target);
    }
  }

  function draw(scene, settled = false) {
    const element = scene.element;
    if (element.dataset.demo === "hero") {
      const seconds = (scene.elapsed % 18000) / 1000;
      const completed = settled
        ? 2
        : seconds >= 10.2
          ? 3
          : seconds >= 3.6
            ? 2
            : seconds >= 1.8
              ? 1
              : 0;
      element
        .querySelectorAll(".preview-task")
        .forEach((row, index) =>
          row.classList.toggle("done", index < completed),
        );
      setText(
        element.querySelector(".preview-chip"),
        t("{n} of {total} complete", { n: completed, total: 3 }),
      );
      const running = !settled && seconds >= 5.4;
      element
        .querySelector(".preview-bottom")
        .classList.toggle("is-running", running);
      if (scene.running !== running) {
        scene.running = running;
        element.querySelector(".preview-play").innerHTML = icon(
          running ? "pause" : "play",
        );
      }
      setText(
        element.querySelector("[data-demo-time]"),
        formatTime(1500 - (running ? Math.floor(seconds - 5.4) : 0)),
      );
      const cursor = element.querySelector(".demo-cursor");
      cursor.classList.toggle(
        "is-visible",
        !settled && seconds >= 0.4 && seconds < 13,
      );
      cursor.classList.toggle(
        "is-clicking",
        !settled &&
          [1.8, 3.6, 5.4, 10.2].some(
            (time) => seconds >= time && seconds < time + 0.45,
          ),
      );
      const cue =
        seconds >= 8.8 ? 3 : seconds >= 4.2 ? 2 : seconds >= 2.3 ? 1 : 0;
      if (scene.cue !== cue) {
        scene.cue = cue;
        const target =
          cue === 2
            ? element.querySelector(".preview-play")
            : element.querySelectorAll(".preview-task > span")[
                cue === 3 ? 2 : cue
              ];
        positionCursor(scene, target);
      }
      element
        .querySelector(".preview-floating")
        .classList.toggle("demo-success", completed === 3);
      setText(
        element.querySelector("[data-demo-caption]"),
        t(
          completed === 3
            ? "Your day, well done."
            : "Every day, a step forward.",
        ),
      );
    } else if (element.dataset.demo === "plans") {
      const phase = scene.elapsed % 11000;
      element
        .querySelectorAll(".demo-plan-row")
        .forEach((row, index) =>
          row.classList.toggle(
            "done",
            settled ? index < 2 : phase >= [1700, 4300, 7000][index],
          ),
        );
      const cue = phase >= 5200 ? 2 : phase >= 2500 ? 1 : 0;
      moveCursor(
        scene,
        element.querySelectorAll(".demo-plan-row > span")[cue],
        cue,
        !settled && phase < 8500,
        [1700, 4300, 7000].some((time) => phase >= time && phase < time + 400),
      );
    } else if (element.dataset.demo === "focus") {
      setText(
        element.querySelector("[data-demo-time]"),
        formatTime(
          1500 - (settled ? 0 : Math.floor((scene.elapsed % 18000) / 1000)),
        ),
      );
    } else if (element.dataset.demo.startsWith("example-"))
      drawGuideExample(scene, settled, { setText, formatTime, moveCursor });
    else if (element.dataset.demo.startsWith("guide-"))
      drawGuideDemo(scene, settled, { setText, formatTime, moveCursor });
    else if (element.dataset.demo.startsWith("product-"))
      drawProductDemo(scene, settled, { setText, formatTime, moveCursor });
    else drawTour(scene, settled, { setText, formatTime, moveCursor });
  }

  function tick() {
    const now = performance.now(),
      delta = Math.min(now - previous, 300);
    previous = now;
    for (const scene of scenes) {
      if (!scene.visible) continue;
      scene.elapsed += delta;
      draw(scene);
    }
    timer = setTimeout(tick, 100);
  }

  function sync() {
    clearTimeout(timer);
    const playing = canPlay();
    for (const scene of scenes)
      scene.element.dataset.motion =
        playing && scene.visible ? "playing" : "paused";
    if (playing && scenes.some((scene) => scene.visible)) {
      previous = performance.now();
      timer = setTimeout(tick, 100);
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const scene = scenes.find((item) => item.element === entry.target);
        scene.visible = entry.isIntersecting;
      }
      sync();
    },
    { threshold: 0.15 },
  );
  scenes.forEach((scene) => {
    draw(scene, motion.matches);
    observer.observe(scene.element);
  });
  const resize = new ResizeObserver(() => {
    for (const scene of scenes) {
      scene.cue = -1;
      draw(scene, motion.matches);
      scene.element
        .querySelector(".demo-cursor")
        ?.getAnimations()
        .forEach((animation) => animation.finish());
    }
  });
  scenes.forEach((scene) => resize.observe(scene.element));
  const presentation = new MutationObserver(sync);
  presentation.observe(document.body, {
    attributes: true,
    attributeFilter: ["data-presentation"],
  });
  const reduce = () => {
    if (motion.matches)
      root
        .querySelectorAll("[data-reveal]")
        .forEach((element) => element.classList.add("is-revealed"));
    scenes.forEach((scene) => {
      scene.elapsed = 0;
      scene.cue = -1;
      draw(scene, motion.matches);
    });
    sync();
  };
  const reveals = [
    ...root.querySelectorAll(
      ".dayx-hero-copy, .dayx-preview-stage, .dayx-value-strip > span, .dayx-section-intro, .dayx-feature, .product-jump-links, .product-story-copy, .product-scene, .product-companion-heading, .dayx-tour-card, .dayx-how li, .dayx-price-copy, .dayx-price-card, .dayx-guide-link, .dayx-final-cta > *, .dayx-footer, .guide-hero-copy, .guide-example, .guide-article > section",
    ),
  ];
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.06, rootMargin: "0px 0px -24px 0px" },
  );
  reveals.forEach((element, index) => {
    element.dataset.reveal = "";
    element.style.setProperty(
      "--reveal-delay",
      `${element.matches(".dayx-feature, .dayx-tour-card, .dayx-guide-link") ? (index % 3) * 70 : 0}ms`,
    );
    if (
      motion.matches ||
      element.getBoundingClientRect().top < innerHeight - 24
    )
      element.classList.add("is-revealed");
    else revealObserver.observe(element);
  });
  const revealFocus = (event) =>
    event.target.closest("[data-reveal]")?.classList.add("is-revealed");
  root.addEventListener("focusin", revealFocus);
  document.addEventListener("visibilitychange", sync);
  motion.addEventListener("change", reduce);
  const header = root.querySelector(".dayx-landing-nav");
  const sections = [...root.querySelectorAll(".landing-desktop-links a")].map(
    (link) => root.querySelector(link.getAttribute("href")),
  );
  let scrollFrame;
  const updateNavigation = () => {
    scrollFrame = null;
    const length = document.documentElement.scrollHeight - innerHeight;
    header.style.setProperty(
      "--landing-scroll",
      String(length > 0 ? Math.min(1, Math.max(0, scrollY / length)) : 0),
    );
    const current = sections
      .filter(
        (section) =>
          section &&
          section.getBoundingClientRect().top <= header.offsetHeight + 80,
      )
      .sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
      )
      .at(-1)?.id;
    header.querySelectorAll('nav a[href^="#"]').forEach((link) => {
      if (link.hash === "#" + current)
        link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });
  };
  const onScroll = () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateNavigation);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  updateNavigation();
  sync();
  dispose = () => {
    clearTimeout(timer);
    observer.disconnect();
    resize.disconnect();
    presentation.disconnect();
    revealObserver.disconnect();
    root.removeEventListener("focusin", revealFocus);
    document.removeEventListener("visibilitychange", sync);
    motion.removeEventListener("change", reduce);
    window.removeEventListener("scroll", onScroll);
    cancelAnimationFrame(scrollFrame);
  };
}
